import { Link } from "react-router-dom";
import type { ArticleBlock } from "@/pages/learn/types";

function Block({ block, id }: { block: ArticleBlock; id?: string }) {
  if (block.kind === "h") {
    return (
      <h2
        id={id}
        className="mt-10 flex scroll-mt-[120px] items-start gap-3 font-heading text-[19px] font-bold leading-[1.6] text-foreground-950 md:text-[21px]"
      >
        <span className="mt-[9px] h-[3px] w-6 shrink-0 rounded-full bg-primary-500" />
        {block.text}
      </h2>
    );
  }

  if (block.kind === "p") {
    return (
      <p className="mt-4 text-[14px] leading-[2] text-foreground-700 md:text-[14.5px]">
        {block.text}
      </p>
    );
  }

  if (block.kind === "list") {
    return (
      <ul className="mt-4 space-y-2.5">
        {block.items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2.5 text-[13.5px] leading-[1.95] text-foreground-700 md:text-[14px]"
          >
            <i className="ri-check-line mt-[5px] shrink-0 text-[15px] text-primary-600" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (block.kind === "note") {
    return (
      <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-primary-200 bg-primary-50 px-5 py-4">
        <i className="ri-information-line mt-0.5 shrink-0 text-[17px] text-primary-700" />
        <div>
          <p className="font-heading text-[13.5px] font-bold text-primary-900">{block.title}</p>
          <p className="mt-1.5 text-[12.5px] leading-[1.9] text-foreground-700 md:text-[13px]">
            {block.text}
          </p>
        </div>
      </div>
    );
  }

  if (block.kind === "pending") {
    return (
      <div className="mt-6 rounded-[10px] border border-dashed border-background-300 bg-background-100 px-5 py-5">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-background-50 px-3 py-1 text-[11px] font-semibold text-foreground-600">
          <i className="ri-time-line text-[13px]" />
          記事準備中
        </p>
        <p className="mt-3 font-heading text-[14px] font-bold text-foreground-950">{block.title}</p>
        <p className="mt-1.5 text-[12.5px] leading-[1.9] text-foreground-600 md:text-[13px]">
          {block.text}
        </p>
      </div>
    );
  }

  if (block.kind === "table") {
    return (
      <div className="mt-5 overflow-x-auto rounded-[10px] border border-background-200">
        <table className="w-full min-w-[520px] border-collapse text-left">
          <thead>
            <tr className="bg-background-100">
              {block.head.map((cell) => (
                <th
                  key={cell}
                  className="px-4 py-3 font-heading text-[12.5px] font-bold text-foreground-900"
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row) => (
              <tr key={row.join("-")} className="border-t border-background-200 bg-background-50">
                {row.map((cell, cellIndex) => (
                  <td
                    key={`${cellIndex}-${cell}`}
                    className="px-4 py-3 align-top text-[12.5px] leading-[1.85] text-foreground-700"
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.kind === "cards") {
    return (
      <div className="mt-5 grid gap-3.5 sm:grid-cols-2">
        {block.items.map((item) => (
          <div
            key={item.title}
            className="rounded-[10px] border border-background-200 bg-background-50 p-5"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-50 text-primary-700">
              <i className={`${item.icon} text-[18px]`} />
            </span>
            <p className="mt-3.5 font-heading text-[14px] font-bold text-foreground-950">
              {item.title}
            </p>
            <p className="mt-1.5 text-[12.5px] leading-[1.9] text-foreground-700">
              {item.text}
            </p>
          </div>
        ))}
      </div>
    );
  }

  if (block.kind === "cta") {
    return (
      <div className="mt-7 rounded-[10px] border border-primary-200 bg-primary-50 px-5 py-5">
        <p className="font-heading text-[14px] font-bold text-primary-900">{block.title}</p>
        <p className="mt-2 text-[12.5px] leading-[1.95] text-foreground-700">{block.text}</p>
        <Link
          to={block.to}
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-700 px-5 py-2.5 text-[12.5px] font-semibold text-background-50 transition-colors hover:bg-primary-800"
        >
          {block.label}
          <i className="ri-arrow-right-line text-[14px]" />
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-9 rounded-[10px] border border-background-200 bg-background-100 px-5 py-5">
      <p className="flex items-center gap-2 font-heading text-[13px] font-bold text-foreground-900">
        <i className="ri-link text-[15px] text-primary-700" />
        出典・参考情報
      </p>
      <ul className="mt-3.5 space-y-3.5">
        {block.items.map((source) => (
          <li key={source.url}>
            <a
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-1.5 text-[12.5px] font-semibold text-primary-800 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-900"
            >
              <i className="ri-external-link-line mt-0.5 shrink-0 text-[13px]" />
              {source.label}
            </a>
            <p className="mt-1 break-all text-[11.5px] leading-[1.7] text-foreground-500">
              確認日：{source.checkedAt}
            </p>
            <p className="mt-1 text-[11.5px] leading-[1.8] text-foreground-600">
              利用条件：{source.terms}
            </p>
          </li>
        ))}
      </ul>
      <p className="mt-4 border-t border-background-200 pt-3 text-[11px] leading-[1.8] text-foreground-500">
        掲載内容は確認時点の情報です。制度や条件は変更される場合があるため、必ずリンク先の公式情報を確認してください。
      </p>
    </div>
  );
}

export default function ArticleView({ blocks }: { blocks: ArticleBlock[] }) {
  let headingIndex = 0;

  return (
    <div>
      {blocks.map((block, index) => {
        let id: string | undefined;
        if (block.kind === "h") {
          headingIndex += 1;
          id = `section-${headingIndex}`;
        }
        return <Block key={`${block.kind}-${index}`} block={block} id={id} />;
      })}
    </div>
  );
}